import React, { useMemo } from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardMetricItem from '@components/pages/Dashboard/DashboardMetricItem.component'
import DashboardSharedTable from '@components/pages/Dashboard/Shared/DashboardSharedTable'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { RecallOverviewResult } from '@services/api/dashboard/recall.api'
import { mapRecallOverviewToMetrics } from '@utils/recallOverview.util'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import { metricDataToItemPropsWithLayoutClass } from '@utils/dashboardMetrics.util'
import {
  getRecallTableRows,
  RECALL_TABLE_COLUMNS,
} from '../dashboardOverview.mockData'
import { getOverviewRecallMetrics } from '../overviewMetrics.mockData'

type RecallCardProps = {
  range: RangeKey
  recallOverview: RecallOverviewResult | null
  loading: boolean
}

export function RecallCard({
  range,
  recallOverview,
  loading,
}: RecallCardProps): React.ReactElement {
  const useMocks = isDashboardDevMocksEnabled()
  const metrics = useMemo(() => {
    if (recallOverview?.kpis) {
      return mapRecallOverviewToMetrics(recallOverview.kpis)
    }
    return withDashboardMetricShellValues(
      getOverviewRecallMetrics(range),
      useMocks,
    )
  }, [recallOverview?.kpis, range, useMocks])
  const tableRows = useMemo(() => {
    if (recallOverview) {
      return (recallOverview.byProvider ?? []).map((row) => ({
        id: row.id,
        name: row.provider,
        completed: String(row.completed),
        overdue: String(row.overdue),
        scheduled: String(row.scheduled),
        recallRate: row.recallRate,
      }))
    }
    return useMocks ? getRecallTableRows(range) : []
  }, [recallOverview, range, useMocks])
  // Effective rendered data: response KPIs/provider rows when present, otherwise
  // the metric shells / table rows (blank when mocks are off).
  const hasData = recallOverview
    ? hasNonZeroDashboardValues(
        recallOverview.kpis.completedRecall,
        recallOverview.kpis.overduePatients,
        recallOverview.kpis.scheduledRecall,
        recallOverview.kpis.recallProduction,
      ) ||
      (recallOverview.byProvider ?? []).some((row) =>
        hasNonZeroDashboardValues(row.completed, row.overdue, row.scheduled),
      )
    : hasNonZeroDashboardValues(...metrics.map((metric) => metric.value)) ||
      tableRows.some((row) =>
        hasNonZeroDashboardValues(row.completed, row.overdue, row.scheduled),
      )
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasData)
  const pending = isDashboardSectionPending(loading, recallOverview != null)

  return (
    <DashboardSectionCard
      title="Recall"
      titleHref="/dashboard/recall"
      className="dashboard-overview-recall-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'tall' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-overview-recall-metrics-row">
        {metrics.map((metric) => (
          <DashboardMetricItem
            key={metric.id ?? metric.leftLabel}
            {...metricDataToItemPropsWithLayoutClass(
              metric,
              'dashboard-overview-recall-metric',
            )}
          />
        ))}
      </div>
      <div className="dashboard-recall-table-wrap">
        <DashboardSharedTable
          loading={pending ? false : loading}
          showSearchFilter={false}
          columns={RECALL_TABLE_COLUMNS}
          data={tableRows as unknown as Record<string, unknown>[]}
          showPagination={false}
        />
      </div>
    </DashboardSectionCard>
  )
}
